import { c as _c } from "react/compiler-runtime";
import type { ActionRequirement } from '@morpho-org/morpho-sdk';
import type { Address } from 'viem';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useWeb3Context } from '@/contexts/Web3Context';
interface UseVaultWithdrawRequirementsParams<T> {
  /** Query-key label, e.g. `'vaultV1Withdraw'` / `'vaultV2Withdraw'`. */
  label: 'vaultV1Withdraw' | 'vaultV2Withdraw';
  vaultAddress: Address;
  /** The sync-built transaction handle; requirements are fetched only while it is set. */
  transaction: T | undefined;
}

/**
 * Fetches a withdraw handle's on-chain requirements (permit2 nonce + `getRequirements()`
 * allowance/authorization reads) from the review dialog — the only consumer that needs them —
 * so typing in the panel never triggers those RPC reads.
 *
 * The handle serializes to `{}` so it cannot sit in the queryKey; a counter bumped on each
 * new handle identity keys the query instead, stable per handle.
 *
 * The handle is rebuilt on every vault poll, so the previous handle's requirements are bridged
 * (`keepPreviousData`) for DISPLAY only — callout, sim prefix, recap. `isBridged` flags the bridge;
 * callers must not sign while it is set, since the retained permit/approval belongs to the
 * superseded handle.
 */
export function useVaultWithdrawRequirements(t0) {
  const $ = _c(24);
  const {
    label,
    vaultAddress,
    transaction
  } = t0;
  const {
    account,
    viemClient
  } = useWeb3Context();
  let t1;
  if ($[0] !== transaction) {
    t1 = () => ({
      transaction,
      seq: 0
    });
    $[0] = transaction;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [handleSeq, setHandleSeq] = useState(t1);
  if (handleSeq.transaction !== transaction) {
    setHandleSeq({
      transaction,
      seq: handleSeq.seq + 1
    });
  }
  const t2 = `${label}-requirements`;
  let t3;
  if ($[2] !== account.address || $[3] !== handleSeq.seq || $[4] !== t2 || $[5] !== vaultAddress || $[6] !== viemClient.uid) {
    t3 = [t2, vaultAddress, account.address, viemClient.uid, handleSeq.seq];
    $[2] = account.address;
    $[3] = handleSeq.seq;
    $[4] = t2;
    $[5] = vaultAddress;
    $[6] = viemClient.uid;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== transaction) {
    t4 = async () => (await (transaction as {
      getRequirements?: () => Promise<readonly ActionRequirement[] | undefined>;
    })?.getRequirements?.()) ?? [];
    $[8] = transaction;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = !!transaction && !!account.address;
  let t6;
  if ($[10] !== account.address || $[11] !== vaultAddress || $[12] !== viemClient.uid) {
    t6 = (previousData, previousQuery) => {
      if (previousData === undefined || !previousQuery) {
        return;
      }
      const [, prevVault, prevAccount, prevClientUid] = previousQuery.queryKey;
      return prevVault === vaultAddress && prevAccount === account.address && prevClientUid === viemClient.uid ? previousData : undefined;
    };
    $[10] = account.address;
    $[11] = vaultAddress;
    $[12] = viemClient.uid;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t3 || $[15] !== t4 || $[16] !== t5 || $[17] !== t6) {
    t7 = {
      queryKey: t3,
      queryFn: t4,
      enabled: t5,
      gcTime: 0,
      placeholderData: t6
    };
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const {
    data: requirements,
    error,
    isLoading,
    isPlaceholderData
  } = useQuery(t7);
  let t8;
  if ($[19] !== error || $[20] !== isLoading || $[21] !== isPlaceholderData || $[22] !== requirements) {
    t8 = {
      requirements,
      requirementsError: error,
      isLoading,
      isBridged: isPlaceholderData
    };
    $[19] = error;
    $[20] = isLoading;
    $[21] = isPlaceholderData;
    $[22] = requirements;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}